import React, {useState} from 'react';
import PropTypes from 'prop-types';

import storefrontRequestHeaders, {
  cartDiscountCodesUpdate,
  cartGiftCardCodesUpdate,
} from '../../../utils/storefrontAPIQueries';

const Discount = ({appliedDiscounts, setAppliedDiscounts}) => {
  // Raw code input string
  const [rawCode, setRawCode] = useState(appliedDiscounts);
  const [activeGiftcards, setActiveGiftcards] = useState([]);
  const [activeDiscounts, setActiveDiscounts] = useState([]);
  const [messages, setMessages] = useState([]);

  // Split the codes and remove spaces
  const codes = rawCode.split(',').map((code) => code.replace(/ /g, ''));

  // Handle the discount form submit
  const handleDiscountSubmit = async (event) => {
    event.preventDefault();

    // Gift card codes are 16 characters long
    const isGiftCard = (code) => code.length === 16;

    // Filter the codes into gift card codes and discount codes
    const giftCardCodes = codes.filter(isGiftCard);
    const discountCodes = codes.filter((code) => !isGiftCard(code));

    // Create the URL and options for the API call
    const url = `https://${window.Shopify.shop}/api/${window.theme.storefrontAPI.version}/graphql.json`;
    const options = {
      method: 'POST',
      headers: storefrontRequestHeaders,
    };

    // Create the promises array for the API calls
    const promises = [];
    const messages = [];

    try {
      // If there are gift card codes, create a promise for the API call
      if (giftCardCodes.length > 0) {
        // Merge the gift card codes with the existing gift card codes
        const newActiveGiftcards = [...activeGiftcards, ...giftCardCodes].reduce((codes, code) => {
          if (!codes.includes(code)) {
            codes.push(code);
          }

          return codes;
        }, []);

        setActiveGiftcards(newActiveGiftcards);

        // Create a promise for the API call
        const giftCardCodesResponse = fetch(url, {
          ...options,
          body: cartGiftCardCodesUpdate(newActiveGiftcards),
        });

        promises.push(giftCardCodesResponse);
      }

      // If there are discount codes, create a promise for the API call
      if (discountCodes.length > 0) {
        // Merge the discount codes with the existing discount codes
        const newActiveDiscounts = [...activeDiscounts, ...discountCodes].reduce((codes, code) => {
          if (!codes.includes(code)) {
            codes.push(code);
          }

          return codes;
        }, []);

        setActiveDiscounts(newActiveDiscounts);

        const discountCodesResponse = fetch(url, {
          ...options,
          body: cartDiscountCodesUpdate(newActiveDiscounts),
        });

        promises.push(discountCodesResponse);
      }

      // If there are promises, wait for all of them to resolve
      if (promises.length > 0) {
        // Wait for all of the promises to resolve
        const responses = await Promise.all(promises);
        const jsonResponses = await Promise.all(responses.map((response) => response.json()));

        jsonResponses.forEach(async (json) => {
          if (!json.data) {
            console.error('Response data is not defined', json);
            return;
          }

          if (json.data.cartDiscountCodesUpdate) {
            json.data.cartDiscountCodesUpdate.cart.discountCodes.forEach((discount) => {
              if (discount.applicable) {
                return;
              }

              if (discount.code === '') {
                return;
              }

              // Remove unapplicable discount codes
              setActiveDiscounts(activeDiscounts.filter((code) => code !== discount.code));

              messages.push(`${discount.code} is not applicable`);
            });
          }
        });

        setMessages(messages);

        // Pass the state back to the side cart
        setAppliedDiscounts(rawCode);

        // Clear the input
        setRawCode('');
      }
    } catch (error) {
      console.error('Error', error);
    }
  };

  return (
    <div className="side-cart__discount">
      <div className="side-cart__discount-content side-cart__section">
        <form className="side-cart__discount-form" onSubmit={handleDiscountSubmit}>
          <input
            type="text"
            value={rawCode}
            className="side-cart__discount-input"
            onChange={(e) => setRawCode(e.target.value)}
            placeholder="Discount code or gift card"
          />
          <button className="side-cart__discount-submit btn" type="submit">
            Apply
          </button>
        </form>
        {messages.length > 0 && (
          <div className="side-cart__discount-messages">
            {messages.map((message) => (
              <p key={message} className="small">
                {message}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

Discount.propTypes = {
  discount: PropTypes.string,
  setDiscount: PropTypes.func.isRequired,
};

export default Discount;
